import { useState } from 'react';

const projects = [
  {
    id: 'domino',
    n: '01',
    title: 'Domino House',
    subtitle: 'A home & workshop for Dale Chihuly',
    year: '2024',
    description:
      'A home/workshop for the glass artist Dale Chihuly using the domino house structure. The concept took inspiration from mushrooms found naturally on the site of Avon Gorge — their lines became the skeleton of the roof, filled with glass panels so the view could pour in.',
    cover: '/uploads/2025/01/Project_4.png',
    images: [
      '/uploads/2025/01/Project_4.png',
      '/uploads/2025/01/Project_1.jpg',
      '/uploads/2025/01/Project_7.jpg',
      '/uploads/2025/01/Project_5.jpg',
      '/uploads/2025/01/Project_6.jpg',
      '/uploads/2025/01/Project_2.png',
      '/uploads/2025/01/Project_3.png',
    ],
  },
  {
    id: 'sanctuary',
    n: '02',
    title: 'Sanctuary',
    subtitle: 'A spiritual room at Bower Ashton',
    year: '2024',
    description:
      'A sanctuary space for students inside a hexagonal room at Bower Ashton campus. A spiritual place to connect with others and with oneself — taking inspiration from churches and the element of stained glass.',
    cover: '/uploads/2025/01/Sanctuary_3.jpg',
    images: [
      '/uploads/2025/01/Sanctuary_3.jpg',
      '/uploads/2025/01/Sanctuary_2.jpg',
      '/uploads/2025/01/Sanctuary_1.jpg',
      '/uploads/2025/01/Sanctuary_4.jpg',
      '/uploads/2025/01/Sanctuary_5.jpg',
      '/uploads/2025/01/Sanctuary_6.jpg',
    ],
  },
  {
    id: 'glass',
    n: '03',
    title: 'Glass Sculptures',
    subtitle: 'A studio practice in molten light',
    year: 'Ongoing',
    description:
      'A passion for glass found through interior design. A growing series of hand-blown sculptures that often inform — and sometimes finish — the rooms I design.',
    cover: '/uploads/2025/01/glass_11.png',
    images: [
      '/uploads/2025/01/glass_11.png',
      '/uploads/2025/01/glass_1.png',
      '/uploads/2025/01/glass_2.png',
      '/uploads/2025/01/glass_3.png',
      '/uploads/2025/01/glass_4.png',
      '/uploads/2025/01/glass_5.png',
      '/uploads/2025/01/glass_6.png',
      '/uploads/2025/01/glass_7.png',
      '/uploads/2025/01/glass_8.png',
      '/uploads/2025/01/glass_9.png',
      '/uploads/2025/01/glass_10.png',
    ],
  },
];

export default function PastProjects() {
  const [active, setActive] = useState('domino');
  const current = projects.find((p) => p.id === active);

  return (
    <section id="projects" data-testid="projects-section" className="relative bg-surface">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 reveal">
          <div>
            <span className="kicker">Earlier work</span>
            <h2 className="font-display text-ink mt-3 leading-[0.95] tracking-tight text-[clamp(2.8rem,7vw,6rem)]">
              Past <span className="italic">projects</span>.
            </h2>
          </div>
          <p className="md:max-w-sm text-sub leading-relaxed">
            Three pieces from the studio archive — the spaces and the studio practice that lead
            up to Pod Bristol.
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-14 flex flex-wrap gap-3" role="tablist" data-testid="projects-tabs">
          {projects.map((p) => (
            <button
              key={p.id}
              data-testid={`project-tab-${p.id}`}
              onClick={() => setActive(p.id)}
              role="tab"
              aria-selected={active === p.id}
              className={`group inline-flex items-baseline gap-3 rounded-full border px-5 py-3 transition-all duration-400 ${
                active === p.id
                  ? 'border-ink bg-ink text-bg'
                  : 'border-line bg-bg text-ink hover:border-ink'
              }`}
            >
              <span className="text-[10px] tracking-[0.3em] uppercase opacity-70">{p.n}</span>
              <span className="font-display text-lg md:text-xl tracking-tight">{p.title}</span>
            </button>
          ))}
        </div>

        {/* Active content */}
        <div key={current.id} className="mt-16 grid grid-cols-12 gap-6 animate-fade-up">
          <div className="col-span-12 md:col-span-5">
            <div className="kicker">{current.subtitle}</div>
            <h3 className="font-display text-4xl md:text-5xl mt-3 leading-tight tracking-tight">
              {current.title}.<span className="text-sub"> {current.year}</span>
            </h3>
            <p className="mt-6 text-sub leading-relaxed">{current.description}</p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <div className="overflow-hidden rounded-sm">
              <img
                src={current.cover}
                alt={current.title}
                data-testid={`project-cover-${current.id}`}
                className="w-full h-[58vh] object-cover"
              />
            </div>
          </div>
        </div>

        {/* Mosaic gallery */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {current.images.map((src, i) => (
            <div
              key={src + i}
              className={`overflow-hidden rounded-sm bg-bg ${
                i % 5 === 0 ? 'md:row-span-2 md:col-span-2 aspect-square md:aspect-auto md:h-full' : 'aspect-[4/5]'
              }`}
            >
              <img
                src={src}
                alt={`${current.title} ${i + 1}`}
                data-testid={`project-img-${current.id}-${i}`}
                className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-105"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
